import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ComboBox, type ComboBoxOption } from '@/components/forms/ComboBox';
import { FormField } from '@/components/forms/FormField';
import { useDirtyForm } from '@/hooks/useDirtyForm';
import { apiClient } from '@/lib/api';
import type { ApprovalRequest } from '@/lib/types';
import type { APPayload, APSummary, ExpenseRebateLine, SupplierCandidate } from './types';
import {
  GRNCandidatePicker,
  type GRNCandidate,
} from './GRNCandidatePicker';
import {
  candidatePoNos,
  candidateGrnNo,
  recommendedGrnNos,
} from './grnCandidates';
import {
  SupplierCandidatePicker,
} from './SupplierCandidatePicker';
import {
  supplierCandidateCode,
  supplierCandidateId,
  supplierCandidateName,
  supplierCandidateNtn,
} from './supplierCandidates';

// Wave 4 Gate 4.4 #6: edits values may be `null` to mean "user cleared this
// field" (e.g. emptied the NTN box). The backend's merge layer preserves
// absent keys as "no edit" and treats explicit null as "clear".
export interface APReprocessEdits {
  supplier_name?: string | null;
  supplier_id?: string | null;
  supplier_code?: string | null;
  supplier_candidates_selected?: SupplierCandidate[] | null;
  ntn?: string | null;
  invoice_no?: string | null;
  invoice_date?: string | null;
  confirm_invoice_date?: boolean;
  fbr_number?: string | null;
  po_numbers?: string[] | null;
  grn_numbers?: string[] | null;
  grn_detail_ids?: number[] | null;
  grn_candidates_selected?: GRNCandidate[] | null;
  gross_amount?: number | null;
  tax_amount?: number | null;
  subtotal?: number | null;
  currency?: string | null;
  line_items?: Array<{
    description?: string | null;
    qty?: number | null;
    rate?: number | null;
    amount?: number | null;
  }> | null;
  expense_rebate_lines?: ExpenseRebateLine[] | null;
}

interface FormState {
  supplier_name: string;
  supplier_id: string;
  supplier_code: string;
  supplier_picker_row: SupplierCandidate | null;
  ntn: string;
  invoice_no: string;
  invoice_date: string;
  fbr_number: string;
  po_numbers_text: string;
  grn_numbers_text: string;
  grn_picker_ids: number[];
  grn_picker_rows: GRNCandidate[];
  gross_amount: string;
  tax_amount: string;
  subtotal: string;
  currency: string;
  expense_rebate_lines: ExpenseRebateFormLine[];
}

interface ExpenseRebateFormLine {
  type: 'E' | 'R';
  amount: string;
  narration: string;
  exp_reb_code_id: string;
  charged_to_mode: string;
  charged_to_code_id: string;
  prefilled_from_history?: boolean;
  historical_suggestion_confidence?: string;
  historical_suggestion_reason?: string;
  historical_suggestions?: ExpenseRebateSuggestion[];
}

interface ExpenseRebateSuggestionEvidence {
  purchase_invoice_id?: string | number | null;
  purchase_invoice_ref?: string | null;
  purchase_invoice_date?: string | null;
  supplier?: string | null;
  narration?: string | null;
  amount?: string | number | null;
}

interface ExpenseRebateSuggestion {
  confidence?: string | null;
  confidence_reason?: string | null;
  exp_reb_code_id?: string | number | null;
  exp_reb_code_comb_id?: string | number | null;
  exp_reb_code_display?: string | null;
  charged_to_mode?: string | null;
  charged_to?: string | null;
  charged_to_code_id?: string | number | null;
  charged_to_code_comb_id?: string | number | null;
  charged_to_code_display?: string | null;
  evidence?: ExpenseRebateSuggestionEvidence[];
}

interface AccountCodeOption {
  code_combination_id: string;
  code_combination_value?: string | null;
  code_combination_desc?: string | null;
  display?: string | null;
}

type APRequest = ApprovalRequest<APPayload, APSummary>;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readString(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const trimmed = String(value).trim();
  return trimmed ? trimmed : undefined;
}

function pickValue(...values: unknown[]): string | undefined {
  for (const value of values) {
    const found = readString(value);
    if (found !== undefined) return found;
  }
  return undefined;
}

function readIdValue(value: unknown): string | number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return readString(value) ?? null;
}

function readGemini(payload: APPayload): Record<string, unknown> {
  const payloadRecord = payload as Record<string, unknown>;
  const defaults = asRecord(payloadRecord['default_fields']);
  const g = payloadRecord['gemini_raw'] ?? defaults['gemini_raw'];
  return asRecord(g);
}

function readStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '').trim()).filter(Boolean);
  }
  const found = readString(value);
  return found ? splitCsv(found) : [];
}

function readExpenseRebateLines(payloadRecord: Record<string, unknown>): ExpenseRebateFormLine[] {
  const structuredFields = asRecord(payloadRecord['structured_fields']);
  const raw = structuredFields['expense_rebate_lines'] ?? payloadRecord['expense_rebate_lines'];
  if (!Array.isArray(raw)) return [];
  return raw.map((line) => {
    const record = asRecord(line);
    const type = String(record['type'] ?? record['entry_type'] ?? 'E').trim().toUpperCase();
    return {
      type: type === 'R' ? 'R' : 'E',
      amount: record['amount'] == null ? '' : String(record['amount']),
      narration: record['narration'] == null ? '' : String(record['narration']),
      exp_reb_code_id:
        record['exp_reb_code_id'] == null && record['exp_reb_code_comb_id'] == null
          ? ''
          : String(record['exp_reb_code_id'] ?? record['exp_reb_code_comb_id']),
      charged_to_mode:
        record['charged_to_mode'] == null && record['charged_to'] == null
          ? ''
          : String(record['charged_to_mode'] ?? record['charged_to']),
      charged_to_code_id:
        record['charged_to_code_id'] == null && record['charged_to_code_comb_id'] == null
          ? ''
          : String(record['charged_to_code_id'] ?? record['charged_to_code_comb_id']),
      prefilled_from_history: record['prefilled_from_history'] === true,
      historical_suggestion_confidence: readString(
        record['historical_suggestion_confidence'],
      ),
      historical_suggestion_reason: readString(record['historical_suggestion_reason']),
      historical_suggestions: readHistoricalSuggestions(
        record['historical_suggestions'],
      ),
    };
  });
}

function readHistoricalSuggestions(value: unknown): ExpenseRebateSuggestion[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const record = asRecord(item);
    const evidence = Array.isArray(record['evidence'])
      ? record['evidence'].map((row) => asRecord(row) as ExpenseRebateSuggestionEvidence)
      : [];
    return {
      confidence: readString(record['confidence']) ?? null,
      confidence_reason: readString(record['confidence_reason']) ?? null,
      exp_reb_code_id: readIdValue(
        record['exp_reb_code_id'] ?? record['exp_reb_code_comb_id'],
      ),
      exp_reb_code_comb_id: readIdValue(
        record['exp_reb_code_comb_id'] ?? record['exp_reb_code_id'],
      ),
      exp_reb_code_display: readString(record['exp_reb_code_display']) ?? null,
      charged_to_mode:
        readString(record['charged_to_mode']) ?? readString(record['charged_to']) ?? null,
      charged_to: readString(record['charged_to']) ?? readString(record['charged_to_mode']) ?? null,
      charged_to_code_id: readIdValue(
        record['charged_to_code_id'] ?? record['charged_to_code_comb_id'],
      ),
      charged_to_code_comb_id: readIdValue(
        record['charged_to_code_comb_id'] ?? record['charged_to_code_id'],
      ),
      charged_to_code_display: readString(record['charged_to_code_display']) ?? null,
      evidence,
    };
  });
}

function suggestionCodeId(suggestion: ExpenseRebateSuggestion): string {
  return String(suggestion.exp_reb_code_id ?? suggestion.exp_reb_code_comb_id ?? '');
}

function suggestionChargedToCodeId(suggestion: ExpenseRebateSuggestion): string {
  return String(
    suggestion.charged_to_code_id ?? suggestion.charged_to_code_comb_id ?? '',
  );
}

function suggestionMode(suggestion: ExpenseRebateSuggestion): string {
  return String(suggestion.charged_to_mode ?? suggestion.charged_to ?? '');
}

function suggestionTitle(suggestion: ExpenseRebateSuggestion): string {
  return (
    suggestion.exp_reb_code_display ||
    suggestionCodeId(suggestion) ||
    'Historical suggestion'
  );
}

function evidenceLabel(evidence: ExpenseRebateSuggestionEvidence | undefined): string {
  if (!evidence) return '';
  const pi = evidence.purchase_invoice_id ? `PI ${evidence.purchase_invoice_id}` : null;
  const ref = evidence.purchase_invoice_ref ? String(evidence.purchase_invoice_ref) : null;
  const amount =
    evidence.amount === null || evidence.amount === undefined || evidence.amount === ''
      ? null
      : `amount ${evidence.amount}`;
  return [pi, ref, evidence.purchase_invoice_date, amount].filter(Boolean).join(' · ');
}

function accountCodeOptionLabel(item: AccountCodeOption): string {
  return [
    item.code_combination_id,
    item.code_combination_value,
    item.code_combination_desc,
  ]
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(' · ');
}

function csvFromString(value: unknown): string {
  if (value == null) return '';
  return String(value);
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function uniqueStrings(values: Array<string | number | null | undefined>): string[] {
  return [
    ...new Set(
      values
        .map((v) => String(v ?? '').trim())
        .filter((v) => v && v !== '—'),
    ),
  ];
}

function normalizeFallbackGrnCandidates(candidates: GRNCandidate[]): GRNCandidate[] {
  const amountValue = (value: unknown): string | number | null | undefined =>
    typeof value === 'string' || typeof value === 'number' || value === null
      ? value
      : undefined;

  return candidates.map((candidate) => {
    if (
      candidate.grn_amount !== undefined ||
      candidate.GRN_AMOUNT !== undefined ||
      candidate.amount !== undefined ||
      candidate.AMOUNT !== undefined
    ) {
      return candidate;
    }
    const fallbackAmount =
      amountValue(candidate.grn_gross_amount) ??
      amountValue(candidate.GRN_GROSS_AMOUNT) ??
      amountValue(candidate.grn_net_amount) ??
      amountValue(candidate.GRN_NET_AMOUNT);
    return fallbackAmount === undefined
      ? candidate
      : { ...candidate, grn_amount: fallbackAmount };
  });
}

const amountFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

function parseFiniteAmount(value: string): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatCheckAmount(value: number): string {
  return amountFormatter.format(Math.round(value * 100) / 100);
}

function selectedDetailIds(row: GRNCandidate): number[] {
  const rawValues = [
    row.grn_detail_ids,
    row.GRN_DETAIL_IDS,
    row.selected_grn_detail_ids,
    row.SELECTED_GRN_DETAIL_IDS,
    row.grn_det_id,
    row.GRN_DET_ID,
    row.grn_detail_id,
    row.GRN_DETAIL_ID,
  ];
  const ids = rawValues
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .flatMap((value) => String(value ?? '').split(','))
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isFinite(value) && value > 0);
  return [...new Set(ids)];
}

function normalizedBucket(request: APRequest, payloadRecord: Record<string, unknown>): string {
  const raw =
    request.bucket ??
    payloadRecord['bucket'] ??
    payloadRecord['error_type'] ??
    payloadRecord['oracle_classification'];
  return String(raw ?? '').trim().toUpperCase();
}

function normalizedLifecycle(request: APRequest): string {
  return String((request.summary as Record<string, unknown> | undefined)?.['lifecycle'] ?? '')
    .trim()
    .toLowerCase();
}

function isTerminalLifecycle(lifecycle: string): boolean {
  return lifecycle === 'posted' || lifecycle === 'rejected';
}

function isSupplierCorrectionBucket(bucket: string): boolean {
  return bucket === 'SUPPLIER_NOT_FOUND' || bucket === 'SUPPLIER_AMBIGUOUS';
}

function isGrnCorrectionBucket(bucket: string): boolean {
  return [
    'GRN_UNAPPROVED',
    'GRN_NOT_FOUND',
    'NO_GRN_FOUND',
    'MULTIPLE_GRNS',
    'MULTIPLE_GOODS_RECEIPTS',
    'MULTIPLE_GRN_FOUND',
    'MULTIPLE_GRN_TOTAL_MATCH_AMBIGUOUS',
    'MULTIPLE_GRN_TOTAL_MATCH_NEEDS_REVIEW',
    'AMOUNT_MISMATCH',
    'PO_GRN_AMOUNT_MISMATCH_NEEDS_REVIEW',
    'GRN_AMOUNT_MISMATCH',
    'MIXED_PI_BOOK_CONTEXT',
    'AMOUNT_ONLY_GRN_MATCH_NEEDS_REVIEW',
    'GOODS_RECEIPT_REVIEW',
    'SINGLE_GRN_TOTAL_MATCH_NEEDS_REVIEW',
    'SUPPLIER_OCR_CORRECTED_GRN_AMOUNT_REVIEW',
  ].includes(bucket);
}

function hasExtractedPoFallback(
  request: APRequest,
  payloadRecord: Record<string, unknown>,
): boolean {
  const structuredFields = asRecord(payloadRecord['structured_fields']);
  const refresh = asRecord(structuredFields['grn_candidate_refresh']);
  return (
    ['GRN_NOT_FOUND', 'GRN_UNAPPROVED'].includes(
      normalizedBucket(request, payloadRecord),
    ) && refresh['fallback'] === 'extracted_po_no_rows_supplier_date'
  );
}

function initialFromRequest(request: APRequest): FormState {
  const payload = (request.payload ?? {}) as APPayload;
  const payloadRecord = payload as Record<string, unknown>;
  const summary = (request.summary ?? {}) as APSummary;
  const defaultFields = asRecord(payloadRecord['default_fields']);
  const gemini = readGemini(payload);
  const extractedPoFallback = hasExtractedPoFallback(request, payloadRecord);

  const pick = (...values: unknown[]): string | undefined => {
    for (const value of values) {
      const found = readString(value);
      if (found !== undefined) return found;
    }
    return undefined;
  };

  const supplierName =
    pick(
      payload.vendor,
      payloadRecord['supplier'],
      defaultFields['vendor'],
      defaultFields['supplier'],
      gemini['supplier'],
    ) ??
    '';

  const ntn =
    pick(
      payloadRecord['supplier_ntn'],
      payload.ntn,
      defaultFields['ntn'],
      defaultFields['supplier_ntn'],
      gemini['supplier_ntn'],
    ) ??
    '';

  const supplierId =
    pick(payloadRecord['supplier_id'], defaultFields['supplier_id']) ?? '';
  const supplierCode =
    pick(payloadRecord['supplier_code'], defaultFields['supplier_code']) ?? '';

  const invoiceNo =
    pick(summary.invoice_no, payload.invoice_no, defaultFields['invoice_no']) ?? '';
  const invoiceDate =
    pick(
      summary.invoice_date,
      payload.invoice_date,
      defaultFields['invoice_date'],
      gemini['date'],
    ) ??
    '';

  const fbr =
    pick(payloadRecord['fbr_number'], defaultFields['fbr_number'], gemini['fbr_number']) ??
    '';

  const poNumbersText = csvFromString(
    (extractedPoFallback
      ? ''
      : pick(summary.po_no, payloadRecord['po_number'], payload.po_no, defaultFields['po_no'])) ??
      '',
  );
  const grnNumbersText = csvFromString(
    pick(summary.grn_no, payload.grn_no, defaultFields['grn_no']) ?? '',
  );

  const grossRaw =
    pick(summary.gross_amount, payload.gross_amount, payloadRecord['amount'], defaultFields['gross_amount']);
  const subtotalRaw =
    pick(payloadRecord['subtotal'], payload.subtotal_before_tax, defaultFields['subtotal_before_tax']);
  const taxRaw = pick(payload.tax_amount, defaultFields['tax_amount']);

  return {
    supplier_name: String(supplierName ?? ''),
    supplier_id: String(supplierId ?? ''),
    supplier_code: String(supplierCode ?? ''),
    supplier_picker_row: null,
    ntn: String(ntn ?? ''),
    invoice_no: String(invoiceNo ?? ''),
    invoice_date: String(invoiceDate ?? '').slice(0, 10),
    fbr_number: String(fbr ?? ''),
    po_numbers_text: poNumbersText,
    grn_numbers_text: grnNumbersText,
    grn_picker_ids: [],
    grn_picker_rows: [],
    gross_amount: grossRaw == null ? '' : String(grossRaw),
    tax_amount: taxRaw == null ? '' : String(taxRaw),
    subtotal: subtotalRaw == null ? '' : String(subtotalRaw),
    currency:
      pick(payloadRecord['currency'], defaultFields['currency']) ?? 'PKR',
    expense_rebate_lines: readExpenseRebateLines(payloadRecord),
  };
}

// Wave 4 Gate 4.4 #6: build edits as a per-field DIFF against the row's
// initial state. Only fields the user actually touched land in the payload;
// for fields they cleared (e.g. emptied NTN) we send explicit `null` so the
// backend's merge layer wipes the underlying payload field instead of
// preserving the stale value (which approve would otherwise post to Oracle).
function diffStringField(
  initial: string,
  current: string,
): string | null | undefined {
  if (current === initial) return undefined;
  return current === '' ? null : current;
}

function diffNumberField(
  initial: string,
  current: string,
): number | null | undefined {
  if (current === initial) return undefined;
  if (current === '') return null;
  const n = Number(current);
  return Number.isFinite(n) ? n : undefined;
}

function diffCsvField(
  initial: string,
  current: string,
): string[] | null | undefined {
  if (current === initial) return undefined;
  const list = splitCsv(current);
  return list.length === 0 ? null : list;
}

function normalizeExpenseRebateLines(
  lines: ExpenseRebateFormLine[],
): ExpenseRebateLine[] {
  return lines.map((line) => {
    const amount = diffNumberField('', line.amount);
    const expRebCodeId = diffNumberField('', line.exp_reb_code_id);
    const chargedToCodeId = diffNumberField('', line.charged_to_code_id);
    return {
      type: line.type,
      amount: amount === undefined ? null : amount,
      narration: line.narration.trim() === '' ? null : line.narration.trim(),
      exp_reb_code_id: expRebCodeId === undefined ? null : expRebCodeId,
      charged_to_mode:
        line.charged_to_mode.trim() === '' ? null : line.charged_to_mode.trim(),
      charged_to_code_id: chargedToCodeId === undefined ? null : chargedToCodeId,
    };
  });
}

function expenseRebateLinesChanged(
  initial: ExpenseRebateFormLine[],
  current: ExpenseRebateFormLine[],
): boolean {
  return (
    JSON.stringify(normalizeExpenseRebateLines(initial)) !==
    JSON.stringify(normalizeExpenseRebateLines(current))
  );
}

function supplierSelectionPatch(selected: SupplierCandidate): Partial<FormState> {
  const next: Partial<FormState> = {
    supplier_picker_row: selected,
  };
  const supplierId = supplierCandidateId(selected);
  const supplierCode = supplierCandidateCode(selected);
  const supplierName = supplierCandidateName(selected);
  const ntn = supplierCandidateNtn(selected);
  if (supplierId) next.supplier_id = supplierId;
  if (supplierCode) next.supplier_code = supplierCode;
  if (supplierName) next.supplier_name = supplierName;
  if (ntn) next.ntn = ntn;
  return next;
}

function stateToEdits(
  state: FormState,
  initial: FormState,
  pickerActive: boolean,
): APReprocessEdits {
  const edits: APReprocessEdits = {};

  const supplierName = diffStringField(initial.supplier_name, state.supplier_name);
  if (supplierName !== undefined) edits.supplier_name = supplierName;

  const supplierId = diffStringField(initial.supplier_id, state.supplier_id);
  if (supplierId !== undefined) edits.supplier_id = supplierId;

  const supplierCode = diffStringField(initial.supplier_code, state.supplier_code);
  if (supplierCode !== undefined) edits.supplier_code = supplierCode;

  if (state.supplier_picker_row) {
    edits.supplier_candidates_selected = [state.supplier_picker_row];
  }

  const ntn = diffStringField(initial.ntn, state.ntn);
  if (ntn !== undefined) edits.ntn = ntn;

  const invoiceNo = diffStringField(initial.invoice_no, state.invoice_no);
  if (invoiceNo !== undefined) edits.invoice_no = invoiceNo;

  const invoiceDate = diffStringField(initial.invoice_date, state.invoice_date);
  if (invoiceDate !== undefined) edits.invoice_date = invoiceDate;

  const fbrNumber = diffStringField(initial.fbr_number, state.fbr_number);
  if (fbrNumber !== undefined) edits.fbr_number = fbrNumber;

  const pickerRowsSelected = pickerActive && state.grn_picker_rows.length > 0;
  if (pickerRowsSelected) {
    const pickerPoNumbers = uniqueStrings(
      state.grn_picker_rows.map((row) => candidatePoNos(row)),
    );
    edits.po_numbers = pickerPoNumbers.length > 0 ? pickerPoNumbers : null;
  } else {
    const poNumbers = diffCsvField(initial.po_numbers_text, state.po_numbers_text);
    if (poNumbers !== undefined) edits.po_numbers = poNumbers;
  }

  if (pickerRowsSelected) {
    edits.grn_numbers = uniqueStrings(
      state.grn_picker_rows
        .map((r) => candidateGrnNo(r))
        .filter((grn) => grn !== '—'),
    );
    const detailIds = state.grn_picker_rows
      .flatMap(selectedDetailIds)
      .sort((a, b) => a - b);
    if (detailIds.length > 0) edits.grn_detail_ids = detailIds;
    edits.grn_candidates_selected = state.grn_picker_rows;
  } else {
    const grns = diffCsvField(initial.grn_numbers_text, state.grn_numbers_text);
    if (grns !== undefined) edits.grn_numbers = grns;
  }

  const gross = diffNumberField(initial.gross_amount, state.gross_amount);
  if (gross !== undefined) edits.gross_amount = gross;

  const tax = diffNumberField(initial.tax_amount, state.tax_amount);
  if (tax !== undefined) edits.tax_amount = tax;

  const subtotal = diffNumberField(initial.subtotal, state.subtotal);
  if (subtotal !== undefined) edits.subtotal = subtotal;

  const currency = diffStringField(initial.currency, state.currency);
  if (currency !== undefined) edits.currency = currency;

  if (
    expenseRebateLinesChanged(
      initial.expense_rebate_lines,
      state.expense_rebate_lines,
    )
  ) {
    edits.expense_rebate_lines =
      state.expense_rebate_lines.length === 0
        ? null
        : normalizeExpenseRebateLines(state.expense_rebate_lines);
  }

  return edits;
}

export interface ReprocessFormHandle {
  state: FormState;
  isDirty: boolean;
  edits: APReprocessEdits;
  reset: () => void;
}

export interface ReprocessFormProps {
  request: APRequest;
  disabled?: boolean;
  onChange?: (handle: ReprocessFormHandle) => void;
  // SUPPLIER_NOT_FOUND bucket forwards a search hit through here; when this
  // changes, the form patches its supplier_* fields with the picked supplier.
  seedSupplier?: SupplierCandidate | null;
}

export function ReprocessForm({ request, disabled, onChange, seedSupplier }: ReprocessFormProps) {
  const initial = useMemo(() => initialFromRequest(request), [request]);
  const { value, patch, isDirty, reset } = useDirtyForm<FormState>(initial);
  const fetchExpenseRebateAccountCodes = useCallback(
    async (query: string): Promise<ComboBoxOption[]> => {
      const trimmed = query.trim();
      if (trimmed.length < 2) return [];
      const response = await apiClient.get(
        '/automations/ap-purchase-invoice/expense-rebate-lovs/account-codes',
        { params: { q: trimmed, limit: 30 } },
      );
      const items = ((response.data as { items?: AccountCodeOption[] })?.items ?? []);
      return items.map((item) => ({
        value: String(item.code_combination_id),
        label: accountCodeOptionLabel(item),
        hint: item.display ?? undefined,
      }));
    },
    [],
  );

  const payload = (request.payload ?? {}) as APPayload;
  const payloadRecord = payload as Record<string, unknown>;
  const structuredFields = asRecord(payloadRecord['structured_fields']);
  const defaultFields = asRecord(payloadRecord['default_fields']);
  const bucket = normalizedBucket(request, payloadRecord);
  const lifecycle = normalizedLifecycle(request);
  const lifecycleLocksGrn = lifecycle === 'pending_approval' || isTerminalLifecycle(lifecycle);
  const supplierCandidatesRaw =
    structuredFields['supplier_candidates'] ?? payloadRecord['supplier_candidates'];
  const supplierCandidates = Array.isArray(supplierCandidatesRaw)
    ? (supplierCandidatesRaw as SupplierCandidate[])
    : [];
  const postableRaw = structuredFields['postable_grn_candidates'];
  const unapprovedRaw = structuredFields['unapproved_grn_candidates'];
  const postableCandidates = Array.isArray(postableRaw)
    ? (postableRaw as GRNCandidate[])
    : [];
  const unapprovedCandidates = Array.isArray(unapprovedRaw)
    ? (unapprovedRaw as GRNCandidate[]).map((candidate) => ({
        ...candidate,
        unapproved_candidate: true,
      }))
    : [];
  const preferredStructuredGrnCandidates = [
    ...postableCandidates,
    ...unapprovedCandidates,
  ];
  const structuredGrnCandidatesRaw = structuredFields['grn_candidates'];
  const structuredGrnCandidates = Array.isArray(structuredGrnCandidatesRaw)
    ? (structuredGrnCandidatesRaw as GRNCandidate[])
    : [];
  const legacyGrnCandidatesRaw = payloadRecord['grn_candidates'];
  const fallbackGrnCandidatesRaw = structuredFields['fallback_grn_candidates'];
  const fallbackGrnCandidates = Array.isArray(fallbackGrnCandidatesRaw)
    ? normalizeFallbackGrnCandidates(fallbackGrnCandidatesRaw as GRNCandidate[])
    : [];
  const grnCandidates =
    preferredStructuredGrnCandidates.length > 0
      ? preferredStructuredGrnCandidates
      : structuredGrnCandidates.length > 0
        ? structuredGrnCandidates
      : Array.isArray(legacyGrnCandidatesRaw)
        ? (legacyGrnCandidatesRaw as GRNCandidate[])
        : fallbackGrnCandidates;
  const liveCandidateRefresh = {
    ...asRecord(structuredFields['grn_candidate_refresh']),
    ...asRecord(structuredFields['live_candidate_refresh']),
  };
  const extractedPoFallback = hasExtractedPoFallback(request, payloadRecord);
  const extractedSupplierReference = extractedPoFallback
    ? csvFromString(
        pickValue(
          (request.summary as Record<string, unknown> | undefined)?.['po_no'],
          payloadRecord['po_number'],
          payload.po_no,
          defaultFields['po_no'],
        ) ?? '',
      )
    : '';
  const recommendedCandidates = recommendedGrnNos(request);
  const recommendedGrnDetailIds = readStringList(
    structuredFields['recommended_grn_detail_ids'],
  );
  const hasPicker = grnCandidates.length > 0;
  const supplierResolved = value.supplier_id.trim() !== '' || value.supplier_code.trim() !== '';
  const hasSupplierPicker =
    supplierCandidates.length > 0 && isSupplierCorrectionBucket(bucket) && !supplierResolved;
  const [grnEditingOpen, setGrnEditingOpen] = useState(false);
  const grnNumberEvidence = uniqueStrings([
    ...readStringList((request.summary as Record<string, unknown> | undefined)?.['grn_no']),
    ...readStringList(payloadRecord['grn_no']),
    ...readStringList(defaultFields['grn_no']),
    ...readStringList(payloadRecord['grn_numbers']),
  ]);
  const grnDetailEvidence = uniqueStrings([
    ...readStringList(payloadRecord['grn_detail_ids']),
    ...readStringList(payloadRecord['selected_grn_detail_ids']),
    ...readStringList(structuredFields['grn_detail_ids']),
  ]);
  const grnResolved = grnNumberEvidence.length > 0 || grnDetailEvidence.length > 0;
  const grnCorrectionBucket = isGrnCorrectionBucket(bucket);
  const grnControlsActive =
    grnEditingOpen || (grnCorrectionBucket && !lifecycleLocksGrn);
  // Explicit GRN-entry mode toggle (Slice 4 Task 4.2). Defaults to picker
  // when candidates exist, manual otherwise. Toggling clears the other
  // mode's selection so the form has a single source of truth.
  const [grnMode, setGrnMode] = useState<'picker' | 'manual'>(
    hasPicker ? 'picker' : 'manual',
  );
  useEffect(() => {
    setGrnMode(hasPicker ? 'picker' : 'manual');
    setGrnEditingOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id, request.updated_at]);
  const pickerSelectionActive =
    grnControlsActive && grnMode === 'picker' && value.grn_picker_rows.length > 0;

  const switchGrnMode = (next: 'picker' | 'manual') => {
    if (next === grnMode) return;
    if (next === 'picker') {
      patch({ grn_numbers_text: '' });
    } else {
      patch({ grn_picker_ids: [], grn_picker_rows: [] });
    }
    setGrnMode(next);
  };
  const selectedSupplierId = value.supplier_picker_row
    ? supplierCandidateId(value.supplier_picker_row)
    : value.supplier_id;
  const resolvedSupplierCandidate = supplierCandidates.find((candidate) => {
    const id = supplierCandidateId(candidate);
    const code = supplierCandidateCode(candidate);
    return (
      (value.supplier_id.trim() !== '' && id === value.supplier_id.trim()) ||
      (value.supplier_code.trim() !== '' && code === value.supplier_code.trim())
    );
  });
  const resolvedSupplierSummary = uniqueStrings([
    resolvedSupplierCandidate
      ? supplierCandidateName(resolvedSupplierCandidate)
      : value.supplier_name,
    value.supplier_code ? `Code ${value.supplier_code}` : '',
    value.supplier_id ? `ID ${value.supplier_id}` : '',
  ]).join(' · ');
  const resolvedGrnSummary = [
    grnNumberEvidence.length > 0 ? `GRN ${grnNumberEvidence.join(', ')}` : null,
    grnDetailEvidence.length > 0 ? `Detail ${grnDetailEvidence.join(', ')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const subtotalNumeric = useMemo(() => {
    const s = Number(value.subtotal);
    return Number.isFinite(s) ? s : null;
  }, [value.subtotal]);
  const grossNumeric = useMemo(() => {
    const s = Number(value.gross_amount);
    return Number.isFinite(s) ? s : null;
  }, [value.gross_amount]);
  const expenseRebateGrossCheck = useMemo(() => {
    if (value.expense_rebate_lines.length === 0) return null;
    const subtotal = parseFiniteAmount(value.subtotal);
    const tax = parseFiniteAmount(value.tax_amount);
    if (subtotal === null || tax === null) return null;

    const totals = value.expense_rebate_lines.reduce(
      (acc, line) => {
        const amount = parseFiniteAmount(line.amount) ?? 0;
        if (line.type === 'E') acc.expenses += amount;
        if (line.type === 'R') acc.rebates += amount;
        return acc;
      },
      { expenses: 0, rebates: 0 },
    );
    const computed = subtotal + tax + totals.expenses - totals.rebates;
    const gross = parseFiniteAmount(value.gross_amount);
    return { subtotal, tax, gross, computed, ...totals };
  }, [
    value.expense_rebate_lines,
    value.gross_amount,
    value.subtotal,
    value.tax_amount,
  ]);

  // Reset the form when the selected request changes.
  useEffect(() => {
    reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.id, request.updated_at]);

  // Apply an externally-provided supplier (SUPPLIER_NOT_FOUND search picker).
  useEffect(() => {
    if (!seedSupplier) return;
    patch(supplierSelectionPatch(seedSupplier));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedSupplier]);

  // Forward the latest derived handle to the parent.
  useEffect(() => {
    const edits = stateToEdits(
      value,
      initial,
      grnControlsActive && grnMode === 'picker',
    );
    if (bucket === 'DATE_REVIEW' && edits.invoice_date) {
      edits.confirm_invoice_date = true;
    }
    onChange?.({
      state: value,
      isDirty,
      edits,
      reset: () => reset(initial),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isDirty, grnMode, grnControlsActive, initial]);

  return (
    <div className="grid grid-cols-1 gap-12 tablet:grid-cols-2">
      <FormField
        kind="text"
        label="Supplier"
        value={value.supplier_name}
        onChange={(v) => patch({ supplier_name: v })}
        disabled={disabled}
      />
      <FormField
        kind="text"
        label="Supplier NTN"
        value={value.ntn}
        onChange={(v) => patch({ ntn: v })}
        disabled={disabled}
      />
      {hasSupplierPicker && (
        <div className="tablet:col-span-2">
          <p className="mb-8 text-sm font-medium text-neutral-700">Supplier candidates</p>
          <SupplierCandidatePicker
            candidates={supplierCandidates}
            selectedId={selectedSupplierId}
            onChange={(selected) => {
              if (!selected) {
                patch({ supplier_picker_row: null });
                return;
              }
              patch(supplierSelectionPatch(selected));
            }}
          />
        </div>
      )}
      {!hasSupplierPicker && supplierResolved && (
        <div className="tablet:col-span-2 rounded-md border border-success-500/30 bg-success-50 px-12 py-8 text-sm text-success-800">
          <p className="font-medium">Resolved supplier</p>
          <p className="mt-2">{resolvedSupplierSummary}</p>
        </div>
      )}
      <FormField
        kind="text"
        label="Invoice no."
        value={value.invoice_no}
        onChange={(v) => patch({ invoice_no: v })}
        disabled={disabled}
      />
      <FormField
        kind="date"
        label="Invoice date"
        value={value.invoice_date}
        onChange={(v) => patch({ invoice_date: v })}
        disabled={disabled}
      />
      <FormField
        kind="text"
        label="FBR number"
        value={value.fbr_number}
        onChange={(v) => patch({ fbr_number: v })}
        disabled={disabled}
      />
      <FormField
        kind="text"
        label="PO number(s)"
        hint={
          extractedPoFallback
            ? 'Enter Shahzad Textile Oracle PO number(s); the scan reference did not match an Oracle PO/GRN.'
            : "Comma-separated when there's more than one."
        }
        value={value.po_numbers_text}
        onChange={(v) => patch({ po_numbers_text: v })}
        disabled={disabled || pickerSelectionActive}
      />
      {extractedSupplierReference ? (
        <FormField
          kind="text"
          label="Supplier ref from scan"
          value={extractedSupplierReference}
          onChange={() => undefined}
          disabled
        />
      ) : null}

      {grnControlsActive ? (
        <div className="tablet:col-span-2 flex flex-wrap items-center gap-8">
          <p className="text-sm font-medium text-neutral-700">GRN entry</p>
          <div className="flex overflow-hidden rounded-md border border-neutral-200">
            <button
              type="button"
              onClick={() => switchGrnMode('picker')}
              disabled={disabled || !hasPicker}
              aria-pressed={grnMode === 'picker'}
              className={
                'px-12 py-6 text-sm transition-colors ' +
                (grnMode === 'picker'
                  ? 'bg-primary-500/10 font-semibold text-primary-700'
                  : 'bg-neutral-0 text-neutral-700 hover:bg-neutral-50') +
                (!hasPicker ? ' opacity-50 cursor-not-allowed' : '')
              }
            >
              Pick from candidates
            </button>
            <button
              type="button"
              onClick={() => switchGrnMode('manual')}
              disabled={disabled}
              aria-pressed={grnMode === 'manual'}
              className={
                'border-l border-neutral-200 px-12 py-6 text-sm transition-colors ' +
                (grnMode === 'manual'
                  ? 'bg-primary-500/10 font-semibold text-primary-700'
                  : 'bg-neutral-0 text-neutral-700 hover:bg-neutral-50')
              }
            >
              Type GRNs manually
            </button>
          </div>
          {!hasPicker && (
            <span className="text-xs text-neutral-500">
              No candidates returned by Oracle — manual entry only.
            </span>
          )}
        </div>
      ) : (
        grnResolved && (
          <div className="tablet:col-span-2 flex flex-wrap items-center justify-between gap-8 rounded-md border border-success-500/30 bg-success-50 px-12 py-8 text-sm text-success-800">
            <div>
              <p className="font-medium">Resolved GRN</p>
              <p className="mt-2">{resolvedGrnSummary}</p>
            </div>
            {!disabled && grnCorrectionBucket && (
              <button
                type="button"
                onClick={() => {
                  setGrnEditingOpen(true);
                  setGrnMode(hasPicker ? 'picker' : 'manual');
                }}
                className="rounded-md border border-success-500/40 bg-neutral-0 px-10 py-5 text-sm font-medium text-success-800 hover:bg-success-50"
              >
                Change GRN
              </button>
            )}
          </div>
        )
      )}

      {grnControlsActive && grnMode === 'picker' && hasPicker && (
        <div className="tablet:col-span-2">
          <GRNCandidatePicker
            candidates={grnCandidates}
            invoiceSubtotal={subtotalNumeric}
            invoiceGross={grossNumeric}
            recommendedGrnNos={recommendedCandidates}
            recommendedGrnDetailIds={recommendedGrnDetailIds}
            liveRefresh={
              Object.keys(liveCandidateRefresh).length > 0 ? liveCandidateRefresh : null
            }
            selectedIds={value.grn_picker_ids}
            onChange={({ ids, rows }) =>
              patch({ grn_picker_ids: ids, grn_picker_rows: rows })
            }
          />
        </div>
      )}

      {grnControlsActive && grnMode === 'manual' && (
        <FormField
          kind="text"
          label="GRN number(s)"
          hint="Comma-separated when there's more than one (e.g. 1883, 1884)."
          className="tablet:col-span-2"
          value={value.grn_numbers_text}
          onChange={(v) => patch({ grn_numbers_text: v })}
          disabled={disabled}
        />
      )}

      <FormField
        kind="number"
        label="Subtotal"
        value={value.subtotal}
        onChange={(v) => patch({ subtotal: v })}
        disabled={disabled}
        step="0.01"
      />
      <FormField
        kind="number"
        label="Tax amount"
        value={value.tax_amount}
        onChange={(v) => patch({ tax_amount: v })}
        disabled={disabled}
        step="0.01"
      />
      <FormField
        kind="number"
        label="Gross amount"
        value={value.gross_amount}
        onChange={(v) => patch({ gross_amount: v })}
        disabled={disabled}
        step="0.01"
      />
      <FormField
        kind="text"
        label="Currency"
        value={value.currency}
        onChange={(v) => patch({ currency: v })}
        disabled={disabled}
      />
      <div className="tablet:col-span-2 flex flex-col gap-8">
        <div className="flex flex-wrap items-center justify-between gap-8">
          <p className="text-sm font-medium text-neutral-700">Exp/Reb lines</p>
          <button
            type="button"
            disabled={disabled}
            onClick={() =>
              patch({
                expense_rebate_lines: [
                  ...value.expense_rebate_lines,
                  {
                    type: 'E',
                    amount: '',
                    narration: '',
                    exp_reb_code_id: '',
                    charged_to_mode: '',
                    charged_to_code_id: '',
                  },
                ],
              })
            }
            className="rounded-md border border-neutral-200 bg-neutral-0 px-10 py-5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add Exp/Reb row
          </button>
        </div>
        {value.expense_rebate_lines.length > 0 && (
          <div className="overflow-x-auto rounded-md border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-xs font-medium uppercase tracking-wide text-neutral-600">
                <tr>
                  <th className="px-8 py-6">Type</th>
                  <th className="px-8 py-6">Amount</th>
                  <th className="px-8 py-6">Narration</th>
                  <th className="px-8 py-6">Exp/Reb code ID</th>
                  <th className="px-8 py-6">Charged-to mode</th>
                  <th className="px-8 py-6">Charged-to code ID</th>
                  <th className="w-32 px-8 py-6"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {value.expense_rebate_lines.map((line, index) => {
                  const rowNo = index + 1;
                  const suggestions = line.historical_suggestions ?? [];
                  const updateLine = (patchLine: Partial<ExpenseRebateFormLine>) => {
                    patch({
                      expense_rebate_lines: value.expense_rebate_lines.map((item, i) =>
                        i === index ? { ...item, ...patchLine } : item,
                      ),
                    });
                  };
                  return (
                    <Fragment key={index}>
                      <tr>
                        <td className="px-8 py-6">
                          <label className="sr-only" htmlFor={`exp-reb-type-${rowNo}`}>
                            Exp/Reb type {rowNo}
                          </label>
                          <select
                            id={`exp-reb-type-${rowNo}`}
                            value={line.type}
                            disabled={disabled}
                            onChange={(e) => updateLine({ type: e.target.value as 'E' | 'R' })}
                            className="h-9 rounded-md border border-neutral-200 bg-neutral-0 px-8 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-neutral-50 disabled:text-neutral-400"
                          >
                            <option value="E">E</option>
                            <option value="R">R</option>
                          </select>
                        </td>
                        <td className="px-8 py-6">
                          <label className="sr-only" htmlFor={`exp-reb-amount-${rowNo}`}>
                            Exp/Reb amount {rowNo}
                          </label>
                          <input
                            id={`exp-reb-amount-${rowNo}`}
                            type="number"
                            step="0.01"
                            value={line.amount}
                            disabled={disabled}
                            onChange={(e) => updateLine({ amount: e.target.value })}
                            className="h-9 w-28 rounded-md border border-neutral-200 bg-neutral-0 px-8 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-neutral-50 disabled:text-neutral-400"
                          />
                        </td>
                        <td className="px-8 py-6">
                          <label className="sr-only" htmlFor={`exp-reb-narration-${rowNo}`}>
                            Exp/Reb narration {rowNo}
                          </label>
                          <input
                            id={`exp-reb-narration-${rowNo}`}
                            type="text"
                            value={line.narration}
                            disabled={disabled}
                            onChange={(e) => updateLine({ narration: e.target.value })}
                            className="h-9 min-w-40 rounded-md border border-neutral-200 bg-neutral-0 px-8 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-neutral-50 disabled:text-neutral-400"
                          />
                        </td>
                        <td className="px-8 py-6">
                          <label className="sr-only" htmlFor={`exp-reb-code-${rowNo}`}>
                            Exp/Reb code id {rowNo}
                          </label>
                          <ComboBox
                            value={line.exp_reb_code_id || null}
                            onChange={(selected) =>
                              updateLine({ exp_reb_code_id: selected ?? '' })
                            }
                            fetchOptions={fetchExpenseRebateAccountCodes}
                            ariaLabel={`Exp/Reb code id ${rowNo}`}
                            placeholder="Search account"
                            emptyText="Type at least 2 characters"
                            disabled={disabled}
                            initialLabel={line.exp_reb_code_id}
                            className="min-w-72"
                          />
                        </td>
                        <td className="px-8 py-6">
                          <label className="sr-only" htmlFor={`charged-to-mode-${rowNo}`}>
                            Charged-to mode {rowNo}
                          </label>
                          <select
                            id={`charged-to-mode-${rowNo}`}
                            value={line.charged_to_mode}
                            disabled={disabled}
                            onChange={(e) =>
                              updateLine({ charged_to_mode: e.target.value })
                            }
                            className="h-9 min-w-32 rounded-md border border-neutral-200 bg-neutral-0 px-8 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-neutral-50 disabled:text-neutral-400"
                          >
                            <option value="">Select</option>
                            <option value="S">S - Supplier</option>
                            <option value="G">G - General</option>
                            {line.charged_to_mode &&
                              !['S', 'G'].includes(line.charged_to_mode) && (
                                <option value={line.charged_to_mode}>
                                  {line.charged_to_mode}
                                </option>
                              )}
                          </select>
                        </td>
                        <td className="px-8 py-6">
                          <label className="sr-only" htmlFor={`charged-to-code-${rowNo}`}>
                            Charged-to code id {rowNo}
                          </label>
                          <ComboBox
                            value={line.charged_to_code_id || null}
                            onChange={(selected) =>
                              updateLine({ charged_to_code_id: selected ?? '' })
                            }
                            fetchOptions={fetchExpenseRebateAccountCodes}
                            ariaLabel={`Charged-to code id ${rowNo}`}
                            placeholder="Search account"
                            emptyText="Type at least 2 characters"
                            disabled={disabled}
                            initialLabel={line.charged_to_code_id}
                            className="min-w-72"
                          />
                        </td>
                        <td className="px-8 py-6">
                          <button
                            type="button"
                            disabled={disabled}
                            onClick={() =>
                              patch({
                                expense_rebate_lines: value.expense_rebate_lines.filter(
                                  (_, i) => i !== index,
                                ),
                              })
                            }
                            className="rounded-md border border-neutral-200 bg-neutral-0 px-8 py-5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Remove Exp/Reb row {rowNo}
                          </button>
                        </td>
                      </tr>
                      {(line.prefilled_from_history || suggestions.length > 0) && (
                        <tr>
                          <td colSpan={7} className="bg-neutral-50 px-8 py-8">
                            <div className="flex flex-col gap-8 rounded-md border border-neutral-200 bg-neutral-0 px-12 py-10 text-sm">
                              {line.prefilled_from_history ? (
                                <div>
                                  <p className="font-medium text-success-800">
                                    Prefilled from history
                                  </p>
                                  <p className="mt-2 text-xs text-neutral-600">
                                    {line.historical_suggestion_reason ||
                                      'A single strong historical coding match was found.'}
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <p className="font-medium text-neutral-800">
                                    Historical suggestions
                                  </p>
                                  {line.historical_suggestion_confidence === 'weak' && (
                                    <p className="mt-2 text-xs text-warning-700">
                                      Weak evidence only. Check the prior PI before using a code.
                                    </p>
                                  )}
                                </div>
                              )}
                              {suggestions.length > 0 && (
                                <div className="flex flex-col gap-6">
                                  {suggestions.map((suggestion, suggestionIndex) => {
                                    const title = suggestionTitle(suggestion);
                                    const evidence = suggestion.evidence?.[0];
                                    return (
                                      <div
                                        key={`${title}-${suggestionIndex}`}
                                        className="flex flex-wrap items-start justify-between gap-8 rounded-md border border-neutral-200 px-10 py-8"
                                      >
                                        <div className="min-w-0">
                                          <p className="font-medium text-neutral-900">
                                            {title}
                                          </p>
                                          <p className="mt-2 text-xs text-neutral-600">
                                            {[
                                              suggestion.confidence
                                                ? String(suggestion.confidence).toUpperCase()
                                                : null,
                                              suggestion.charged_to_code_display ||
                                                suggestionChargedToCodeId(suggestion),
                                              evidenceLabel(evidence),
                                            ]
                                              .filter(Boolean)
                                              .join(' · ')}
                                          </p>
                                        </div>
                                        {!line.prefilled_from_history && (
                                          <button
                                            type="button"
                                            disabled={disabled}
                                            onClick={() =>
                                              updateLine({
                                                exp_reb_code_id: suggestionCodeId(suggestion),
                                                charged_to_mode: suggestionMode(suggestion),
                                                charged_to_code_id:
                                                  suggestionChargedToCodeId(suggestion),
                                              })
                                            }
                                            className="rounded-md border border-primary-500/40 bg-neutral-0 px-10 py-5 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
                                          >
                                            Use suggestion {title}
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {expenseRebateGrossCheck && (
          <div className="rounded-md border border-neutral-200 bg-neutral-50 px-12 py-8 text-sm text-neutral-700">
            <p className="font-medium text-neutral-800">Posting gross check</p>
            <p className="mt-4 font-mono text-neutral-900">
              {formatCheckAmount(expenseRebateGrossCheck.subtotal)} +{' '}
              {formatCheckAmount(expenseRebateGrossCheck.tax)} +{' '}
              {formatCheckAmount(expenseRebateGrossCheck.expenses)} -{' '}
              {formatCheckAmount(expenseRebateGrossCheck.rebates)} ={' '}
              {formatCheckAmount(expenseRebateGrossCheck.computed)}
            </p>
            {expenseRebateGrossCheck.gross !== null && (
              <p className="mt-3 text-xs text-neutral-600">
                Invoice gross remains {formatCheckAmount(expenseRebateGrossCheck.gross)}.
                Exp/Reb is validated separately for Oracle posting.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
